/**
 * Change log button + dropdown for launched / imported campaigns.
 *
 * Lists the campaign's field-change history (recorded server-side from every
 * post-launch edit dispatch) newest-first, with an Undo button next to each
 * reversible change. Undo re-dispatches the change's inverse edit through the
 * same edit pipeline the original used (so the live platform campaign is
 * updated), then marks the change reverted and records an undo row — which is
 * itself undoable (redo) via the same button.
 *
 * API:
 *   GET  /api/v2/campaigns/:id/changelog
 *   POST /api/v2/campaigns/:id/changelog/:changeId/undo
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { api, getApiUrl } from '../../utils/apiConfig';

const SOURCE_LABELS = {
  clw_batch_edit: 'Grid save',
  single_edit: 'Edit',
  apply_actionable_suggestion: 'Optimizer',
  undo: 'Undo',
  import: 'Import',
  launch: 'Launch',
  edit: 'Edit',
};

const OP_LABELS = {
  set_field: 'Set',
  list_add: 'Added',
  list_remove: 'Removed',
  list_edit: 'Edited',
  undo: 'Undone',
  import: 'Imported',
  launch: 'Launched',
  edit: 'Edited',
};

const ENTITY_LABELS = {
  line_item: 'Line item',
  ad_group: 'Ad group',
  asset_group: 'Asset group',
  ad_set: 'Ad set',
  campaign: 'Campaign',
};

function formatValue(v) {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'object') {
    const s = JSON.stringify(v);
    return s && s.length > 60 ? `${s.slice(0, 57)}…` : s;
  }
  const s = String(v);
  return s.length > 60 ? `${s.slice(0, 57)}…` : s;
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function sourceLabel(source) {
  return SOURCE_LABELS[source] || source || 'Edit';
}

function opLabel(op) {
  return OP_LABELS[op] || op || '';
}

export function ChangeRow({ change, undoing, onUndo }) {
  const isUndo = change.source === 'undo';
  const reverted = change.status === 'reverted';
  const fieldName = change.fieldLabel || change.field || null;
  const entityName = change.entityName || null;
  const entityLabel = change.entityType ? ENTITY_LABELS[change.entityType] || change.entityType : null;
  const hasValues = (change.oldValue !== null && change.oldValue !== undefined)
    || (change.newValue !== null && change.newValue !== undefined);
  const description = change.description || null;

  return (
    <div
      style={{
        padding: '8px 10px', borderBottom: '1px solid var(--g2, #eee)', fontSize: 12,
        opacity: reverted ? 0.6 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        <span style={{ fontWeight: 600 }}>{opLabel(change.op)}</span>
        {fieldName && (
          <span style={{ color: 'var(--g4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {fieldName}
          </span>
        )}
        <span
          style={{
            marginLeft: 'auto', flexShrink: 0, padding: '1px 6px', borderRadius: 8,
            background: isUndo ? 'var(--g2, #eee)' : 'var(--g1, #f5f5f5)',
            color: 'var(--g4)', fontSize: 10,
          }}
        >
          {sourceLabel(change.source)}
        </span>
      </div>
      {description && (
        <div style={{ color: 'var(--g4)', marginBottom: 2, wordBreak: 'break-word' }}>{description}</div>
      )}
      {entityName && (
        <div style={{ color: 'var(--g4)', marginBottom: 2 }}>
          {entityLabel ? `${entityLabel}: ` : ''}{entityName}
        </div>
      )}
      {hasValues && (
        <div style={{ color: 'var(--g4)', marginBottom: 4, wordBreak: 'break-word' }}>
          {formatValue(change.oldValue)} <span style={{ color: 'var(--g4)' }}>→</span> {formatValue(change.newValue)}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'var(--g5, #999)' }} title={change.createdAt}>
          {formatTime(change.createdAt)}{change.createdByName ? ` · ${change.createdByName}` : ''}
        </span>
        {reverted && (
          <span style={{ color: 'var(--g5, #999)' }}>· reverted{change.revertedByName ? ` by ${change.revertedByName}` : ''}</span>
        )}
        {change.undoable && !reverted && onUndo && (
          <button
            type="button"
            className="clw-btn clw-btn-secondary clw-btn-sm"
            style={{ marginLeft: 'auto', padding: '1px 8px', fontSize: 11 }}
            onClick={() => onUndo(change)}
            disabled={Boolean(undoing)}
            title="Revert this change in the live campaign"
          >
            {undoing === change.id ? 'Undoing…' : isUndo ? 'Redo' : 'Undo'}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * @param {Object} props
 * @param {string} props.campaignId  internal campaign id
 * @param {string} props.accountId
 * @param {string} props.userId
 */
export default function ChangeLogButton({ campaignId, accountId, userId }) {
  const [open, setOpen] = useState(false);
  const [changes, setChanges] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [undoing, setUndoing] = useState(null);
  const rootRef = useRef(null);

  // Close the dropdown when the user clicks anywhere outside it.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const refresh = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/changelog`), {
        params: { accountId, userId },
      });
      setChanges(Array.isArray(res.data?.changes) ? res.data.changes : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load change log');
      setChanges([]);
    } finally {
      setLoading(false);
    }
  }, [campaignId, accountId, userId]);

  const onToggle = () => {
    if (!open) refresh();
    setOpen((v) => !v);
  };

  const onClickUndo = async (change) => {
    const field = change.fieldLabel || change.field || 'change';
    const action = change.source === 'undo' ? 'Redo' : 'Undo';
    if (!window.confirm(`${action} "${field}"?\n\nThis dispatches the inverse edit to the live campaign.`)) return;
    setUndoing(change.id);
    setError(null);
    setNotice(null);
    try {
      const res = await api.post(
        getApiUrl(`/api/v2/campaigns/${campaignId}/changelog/${change.id}/undo`),
        { accountId, userId },
      );
      if (Array.isArray(res.data?.changes)) setChanges(res.data.changes);
      setNotice(res.data?.message || `${action} dispatched`);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || `${action} failed`);
      await refresh();
    } finally {
      setUndoing(null);
    }
  };

  const undoableCount = Array.isArray(changes)
    ? changes.filter((c) => c.undoable && c.status === 'applied').length
    : 0;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="clw-btn clw-btn-secondary clw-btn-sm"
        onClick={onToggle}
        disabled={!campaignId}
        title="Field-change history for this campaign — undo any change"
      >
        Change log {changes ? `(${changes.length})` : '▾'}
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 1000,
          minWidth: 460, maxWidth: 620, maxHeight: 520, overflowY: 'auto',
          background: 'var(--bg-elevated, #fff)', border: '1px solid var(--g3, #d0d0d0)',
          borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 8,
        }}>
          <div style={{ padding: '6px 8px', fontSize: 12, color: 'var(--g4)', borderBottom: '1px solid var(--g2, #eee)', display: 'flex', gap: 8 }}>
            <span>
              Field changes {loading ? '· loading…' : changes ? `· ${changes.length}` : ''}
            </span>
            {undoableCount > 0 && !loading && (
              <span style={{ color: 'var(--g4)' }}>· {undoableCount} undoable</span>
            )}
          </div>
          {error && <div style={{ padding: 8, color: 'var(--red, #c00)', fontSize: 12 }}>{error}</div>}
          {notice && <div style={{ padding: 8, color: 'var(--green, #0a7a3d)', fontSize: 12 }}>{notice}</div>}
          {!loading && changes && changes.length === 0 && (
            <div style={{ padding: 12, color: 'var(--g4)', fontSize: 12, textAlign: 'center' }}>
              No field changes recorded yet. Post-launch edits will appear here and can be undone.
            </div>
          )}
          {changes && changes.map((c) => (
            <ChangeRow key={c.id} change={c} undoing={undoing} onUndo={onClickUndo} />
          ))}
        </div>
      )}
    </div>
  );
}
